import {
  Dices,
  Globe,
  Heart,
  Home,
  Library,
  ShoppingBag,
  Users,
} from 'lucide-react';
import { useMemo } from 'react';

import { type NavItem } from './AppNav.types';

import { ROUTES } from '@/app/routes';
import { type NavLayout } from '@/tokens/breakpoints';

/**
 * The navigation's composition, taken from the mockups: four entries in the
 * bottom bar, six in the top bar, and the rail alone would surface Community —
 * which is `hidden` for now, see below.
 */
const ALL_ITEMS: readonly NavItem[] = [
  {
    to: ROUTES.dashboard,
    labelKey: 'dashboard',
    // A house rather than a dashboard grid: this is the app's home screen.
    icon: Home,
    layouts: ['tabbar', 'topbar', 'rail'],
  },
  {
    to: ROUTES.library,
    labelKey: 'library',
    icon: Library,
    layouts: ['tabbar', 'topbar', 'rail'],
  },
  {
    to: ROUTES.sessions,
    labelKey: 'sessions',
    icon: Dices,
    layouts: ['tabbar', 'topbar', 'rail'],
  },
  {
    to: ROUTES.store,
    labelKey: 'store',
    icon: ShoppingBag,
    layouts: ['tabbar', 'topbar', 'rail'],
  },
  {
    to: ROUTES.wishlist,
    labelKey: 'wishlist',
    icon: Heart,
    layouts: ['topbar', 'rail'],
  },
  {
    to: ROUTES.family,
    labelKey: 'family',
    icon: Users,
    layouts: ['topbar', 'rail'],
    requiresFamily: true,
  },
  {
    // Hidden until the screen is worth finding: the other features come
    // first. The route still answers and the page still exists — drop this
    // line to bring the entry back.
    to: ROUTES.community,
    labelKey: 'community',
    icon: Globe,
    layouts: ['rail'],
    hidden: true,
  },
];

/**
/**
 * The navigation entry matching a URL, or `null` when the screen is not one.
 *
 * Warning: unlike `useAppNav` it does not filter on household membership —
 * hiding an entry is not forbidding an address.
 *
 * @param pathname The current path.
 * @returns The matching entry, or `null`.
 */
export function findNavItem(pathname: string): NavItem | null {
  return ALL_ITEMS.find((item) => item.to === pathname) ?? null;
}

interface AppNavOptions {
  /**
  /**
   * Whether the reader belongs to a household.
   *
   * Warning: without one, the Family entry disappears everywhere — bar, rail and
   * drawer. A permanent entry to a screen nobody can fill teaches readers to
   * ignore the navigation.
   */
  hasFamily: boolean;
}

/**
 * Toute la logique de navigation, en un seul endroit, quelle que soit la
 * rendered structure. The three presenters decide nothing, which is what stops
 * a new entry being forgotten on one format.
 */
export function useAppNav(layout: NavLayout, { hasFamily }: AppNavOptions) {
  return useMemo(() => {
    // Warning: the conditional filter runs BEFORE the structural one, or an
    // entry dropped here would reappear in the overflow drawer, which collects
    // everything the structure leaves out.
    const available = ALL_ITEMS.filter(
      (item) => !item.hidden && (hasFamily || !item.requiresFamily),
    );
    const items = available.filter((item) => item.layouts.includes(layout));

    return {
      items,
      // Ce qui ne tient pas dans la structure courante part dans le tiroir
      // the drawer on narrow formats, or the overflow menu.
      overflowItems: available.filter((item) => !items.includes(item)),
    };
  }, [layout, hasFamily]);
}
